import { useCreateProjectMutation, useUpdateProjectMutation } from 'api/api';
import { ProjectContext } from 'context/ProjectContext';
import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Project, User } from 'types/types';
import { getRejectedFieldNames, ACCOUNT_UNAVAILABLE } from 'lib/utils/getErrorMessage';
import { buildProjectPatch, clearRejectedFields, ProjectField } from 'lib/utils/projectPatch';
import { hasProjectDataChanged } from 'lib/utils/projectChanges';

interface HookProps {
  userData: User | undefined
}

// isSubmitting and leaveForm are optional parameters used to indicate if the hook should submit the form and leave the form page, respectively
interface UpdaterProps {
  isSubmitting?: boolean
  formValues?: Partial<Project>
  leaveForm?: boolean
  // The fields the calling step owns (STEP_FIELDS[n]). Sending only these stops one
  // rejected value from failing every later step's save (ID 7 / ID 9). Omit to send the
  // whole project.
  fields?: readonly ProjectField[]
  // Called instead of saving when the user cancels the revert-to-Draft prompt. The
  // return value stays `false` for every caller, because nothing was saved either way -
  // this only lets a caller tell "the user said no" apart from "the server said no".
  // Back needs the distinction; Next and Save & Exit hold the user on the step for both
  // and can ignore it.
  onRevertDeclined?: () => void
}

// TODO: There should be a check to see if data has been updated before sending redundant update requests
export const useCreateUpdateProject = ({ userData }: HookProps) => {
  const navigate = useNavigate();
  const [createProject, { error: createError, isLoading: isCreating }] = useCreateProjectMutation();
  const [updateProject, { error: updateError, isLoading: isUpdating }] = useUpdateProjectMutation();

  // Set when the hook declines to send a request at all. The mutation hooks report no
  // error in that case, so without this the caller blocks navigation and shows nothing.
  const [blockedError, setBlockedError] = useState<{ status: string } | null>(null);

  // The save has to stop and wait for an answer, so the dialog's result is delivered
  // through a promise. The resolver is held inside an object because useState treats a
  // bare function argument as an updater and would call it instead of storing it.
  const [revertRequest, setRevertRequest] = useState<{ resolve: (confirmed: boolean) => void } | null>(null);

  const askToRevertToDraft = () => new Promise<boolean>(resolve => setRevertRequest({ resolve }));

  const answerRevert = (confirmed: boolean) => {
    revertRequest?.resolve(confirmed);
    setRevertRequest(null);
  };

  const revertPrompt = {
    open: revertRequest !== null,
    onCancel: () => answerRevert(false),
    onConfirm: () => answerRevert(true),
  };

  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('ProjectContext error');
  }
  const { setProjectId, projectDetails, setProjectDetails, isNewProject } = context;

  // The response is the whole project as stored, so it becomes context. Keeping
  // context equal to saved state is what stops the UI showing a value the row
  // does not hold. Writing `id` here also closes the window where a freshly
  // created project still had id -1 in context (deferred #13).
  const updateContextWithResponse = (response: Project) => {
    setProjectDetails(response);
  };

  // Returns whether the save succeeded, so callers can hold the user on the step and
  // show the error instead of navigating on regardless (ID 7).
  const handleCreateUpdateProject = async ({
    formValues = {},
    isSubmitting = false,
    leaveForm = true,
    fields,
    onRevertDeclined
  }: UpdaterProps): Promise<boolean> => {
    setBlockedError(null);

    if (!userData) {
      console.error("Error fetching user data, unable to save project.");
      setBlockedError({ status: ACCOUNT_UNAVAILABLE });

      return false;
    }

    const updaterIdentifier = `${userData?.first_name} ${userData?.last_name}`;
    const isCreating = projectDetails.id === -1;

    // Create and submit both need the whole project: create() requires the smqs key
    // (serializers.py:371), and submit's cross-field rules only run for fields present
    // in the request (serializers.py:315-317), so narrowing would skip them.
    const patchFields = isCreating || isSubmitting ? undefined : fields;

    let updatedProjectDetails = {
      ...buildProjectPatch({ projectDetails, formValues, fields: patchFields }),
      updated_at: new Date().toISOString(),
      updated_by: updaterIdentifier,
    };

    // The only two payloads that may carry is_submitted, and the only two that know it
    // first-hand rather than remembering it. Submit is the user's intent. Create must
    // send it: ProjectSerializer.validate() dereferences self.instance when the field is
    // absent (serializers.py:305), and self.instance is None on a POST, so omitting it
    // there returns a 500. Order matters - submitting a project that has not been
    // created yet must still submit it.
    //
    // submitted_at and submitted_by are deliberately not sent. validate() overwrites
    // submitted_at (serializers.py:335) and submitted_by is read_only on
    // ProjectSerializerList:267, so both were discarded; sending them only suggested the
    // client had a say in the stamp. It does not - see NOTES #21.
    if (isSubmitting) {
      updatedProjectDetails = { ...updatedProjectDetails, is_submitted: true };
    } else if (isCreating) {
      updatedProjectDetails = { ...updatedProjectDetails, is_submitted: false };
    }

    // The Database Team's request (ID 12 Bug 2 commentary, NOTES #20): changing a
    // submitted project's data returns it to Draft. Confined to this branch on purpose -
    // create and submit already decided the flag above, and a save that changes nothing
    // must leave a submitted project submitted, which is the other half of their sentence.
    if (!isCreating && !isSubmitting && projectDetails.is_submitted
        && hasProjectDataChanged({ projectDetails, patch: updatedProjectDetails })) {
      const confirmed = await askToRevertToDraft();

      // Cancelling is not a failure. The user keeps their edits on the step and the
      // project stays Submitted; returning false holds them there, exactly as a rejected
      // save does.
      //
      // Back is the one caller for which "held there" is not automatic - it is allowed to
      // leave, so a false it cannot explain became a prompt to discard the edits the user
      // had just protected by cancelling. onRevertDeclined tells it which false this is.
      if (!confirmed) {
        onRevertDeclined?.();

        return false;
      }

      updatedProjectDetails = { ...updatedProjectDetails, is_submitted: false };
    }

    try {
      let response: Project;

      if (isCreating) {
        const newProject = {
          ...updatedProjectDetails,
          created_at: new Date().toISOString(),
          created_by: updaterIdentifier,
        };
        delete (newProject as Partial<Project>).id;

        response = await createProject(newProject as Project).unwrap();
        setProjectId(response.id);
        updateContextWithResponse(response);
        leaveForm && navigate('/projects?modal=open&modalType=addProjectSuccess');
      } else {
        response = await updateProject(updatedProjectDetails).unwrap();
        updateContextWithResponse(response);
        if (isNewProject) {
          leaveForm && navigate('/projects?modal=open&modalType=addProjectSuccess');
        } else {
          leaveForm && navigate('/projects?modal=open&modalType=updateProjectSuccess');
        }
      }

      return true;
    } catch (error) {
      console.error('Error submitting form:', error);

      // Drop whatever the backend rejected out of context. Otherwise it stays there for
      // the rest of the session - nothing refetches the project detail, since
      // updateProject invalidates only the Projects LIST tag (api.ts:288) - and every
      // later save that includes the field fails on it too.
      const rejectedFields = getRejectedFieldNames(error);
      if (rejectedFields.length) {
        setProjectDetails(prevDetails => clearRejectedFields(prevDetails, rejectedFields));
      }

      return false;
    }
  };


  const error = createError || updateError || blockedError;
  const isLoading = isCreating || isUpdating;

  return {
    handleCreateUpdateProject,
    error,
    isLoading,
    revertPrompt
  };
};